// ========================================
// pages/Compliance.js - Legal & Regulatory Compliance
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Inform users about our legal and regulatory compliance
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// - /images/icons/feature-security.png - Security/Compliance icons throughout
// - /images/icons/feature-tradition.png - Regulatory icons
// - /images/icons/feature-fraud.png - Data protection icons
// - /images/icons/feature-trustees.png - Certification icons
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const Compliance = () => {
  return (
    <div className="compliance-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO */}
      {/* ======================================== */}
      <section className="compliance-hero">
        <div className="compliance-hero-overlay"></div>
        <div className="compliance-container">
          <div className="compliance-hero-content">
            <div className="compliance-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="compliance-badge-icon" />
              <span>Legal & Regulatory</span>
            </div>
            <h1 className="compliance-hero-title">
              Our <span className="gold">Compliance</span> Commitment
            </h1>
            <p className="compliance-hero-subtitle">
              Agyapadie Vault operates with the highest standards of legal and regulatory compliance. 
              We are committed to protecting your data, respecting your privacy, and operating transparently.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: REGULATORY FRAMEWORK */}
      {/* ======================================== */}
      <section className="compliance-framework">
        <div className="compliance-container">
          <h2 className="compliance-section-title">Regulatory <span className="gold">Framework</span></h2>
          <p className="compliance-section-subtitle">
            We adhere to the following laws, regulations, and industry standards
          </p>
          
          <div className="compliance-framework-grid">
            <div className="compliance-framework-card">
              <img src="/images/icons/feature-security.png" alt="Data Protection" className="compliance-framework-icon" />
              <h3>Data Protection Act, 2012 (Act 843)</h3>
              <p>
                Ghana's primary data protection legislation. We are registered with the Data Protection 
                Commission and comply with all requirements for the collection, processing, and storage 
                of personal data.
              </p>
              <div className="compliance-framework-status">
                <span className="status-badge compliant">Compliant</span>
              </div>
            </div>
            
            <div className="compliance-framework-card">
              <img src="/images/icons/feature-tradition.png" alt="GDPR" className="compliance-framework-icon" />
              <h3>General Data Protection Regulation (GDPR)</h3>
              <p>
                For our users in the European Union, we fully comply with GDPR requirements including 
                the right to access, right to be forgotten, data portability, and breach notification.
              </p>
              <div className="compliance-framework-status">
                <span className="status-badge compliant">Compliant</span>
              </div>
            </div>
            
            <div className="compliance-framework-card">
              <img src="/images/icons/feature-fraud.png" alt="Electronic Transactions" className="compliance-framework-icon" />
              <h3>Electronic Transactions Act, 2008 (Act 772)</h3>
              <p>
                We comply with Ghana's electronic transactions law, ensuring that electronic records 
                and signatures on our platform are legally recognized.
              </p>
              <div className="compliance-framework-status">
                <span className="status-badge compliant">Compliant</span>
              </div>
            </div>
            
            <div className="compliance-framework-card">
              <img src="/images/icons/feature-trustees.png" alt="Bank of Ghana" className="compliance-framework-icon" />
              <h3>Bank of Ghana Directives</h3>
              <p>
                We adhere to Bank of Ghana guidelines on electronic money issuers, cybersecurity, 
                and financial data protection.
              </p>
              <div className="compliance-framework-status">
                <span className="status-badge in-progress">In Progress</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: DATA PROTECTION */}
      {/* ======================================== */}
      <section className="compliance-data-protection">
        <div className="compliance-container">
          <div className="compliance-data-grid">
            <div className="compliance-data-content">
              <h2 className="compliance-section-title">Data <span className="gold">Protection</span></h2>
              <p>
                Your personal data is protected by multiple layers of security and legal compliance.
              </p>
              
              <div className="compliance-data-list">
                <div className="compliance-data-item">
                  <img src="/images/icons/feature-security.png" alt="Encryption" className="compliance-data-icon" />
                  <div>
                    <h4>Encryption at Rest & In Transit</h4>
                    <p>All data is encrypted using AES-256 at rest and TLS 1.3 in transit.</p>
                  </div>
                </div>
                
                <div className="compliance-data-item">
                  <img src="/images/icons/feature-fraud.png" alt="Impact Assessment" className="compliance-data-icon" />
                  <div>
                    <h4>Data Protection Impact Assessments</h4>
                    <p>Regular DPIAs conducted for all new features and data processing activities.</p>
                  </div>
                </div>
                
                <div className="compliance-data-item">
                  <img src="/images/icons/feature-trustees.png" alt="DPO" className="compliance-data-icon" />
                  <div>
                    <h4>Data Protection Officer (DPO)</h4>
                    <p>Appointed DPO oversees all data protection activities and compliance.</p>
                  </div>
                </div>
                
                <div className="compliance-data-item">
                  <img src="/images/icons/feature-tradition.png" alt="DPA" className="compliance-data-icon" />
                  <div>
                    <h4>Data Processing Agreements</h4>
                    <p>Signed DPAs with all third-party vendors who process user data.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="compliance-data-rights">
              <h3>Your Data Rights</h3>
              <ul>
                <li>✓ Right to access your personal data</li>
                <li>✓ Right to rectify inaccurate data</li>
                <li>✓ Right to erasure (right to be forgotten)</li>
                <li>✓ Right to restrict processing</li>
                <li>✓ Right to data portability</li>
                <li>✓ Right to object to processing</li>
                <li>✓ Right to withdraw consent</li>
                <li>✓ Right to lodge a complaint</li>
              </ul>
              <Link to="/privacy" className="compliance-privacy-link">View Full Privacy Policy →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: SECURITY CERTIFICATIONS */}
      {/* ======================================== */}
      <section className="compliance-certifications">
        <div className="compliance-container">
          <h2 className="compliance-section-title white">Security <span className="gold">Certifications</span></h2>
          <p className="compliance-section-subtitle white">
            We maintain industry-standard security certifications and undergo regular audits
          </p>
          
          <div className="compliance-cert-grid">
            <div className="compliance-cert-card">
              <img src="/images/icons/feature-security.png" alt="ISO 27001" className="compliance-cert-icon" />
              <h3>ISO 27001</h3>
              <p>Information Security Management</p>
              <span className="compliance-cert-status">Certified</span>
            </div>
            
            <div className="compliance-cert-card">
              <img src="/images/icons/feature-trustees.png" alt="SOC 2" className="compliance-cert-icon" />
              <h3>SOC 2 Type II</h3>
              <p>Security, Availability, Confidentiality</p>
              <span className="compliance-cert-status">In Progress</span>
            </div>
            
            <div className="compliance-cert-card">
              <img src="/images/icons/feature-fraud.png" alt="PCI DSS" className="compliance-cert-icon" />
              <h3>PCI DSS</h3>
              <p>Payment Card Industry Compliance</p>
              <span className="compliance-cert-status">Via Payment Partner</span>
            </div>
            
            <div className="compliance-cert-card">
              <img src="/images/icons/feature-tradition.png" alt="Ghana Data Protection" className="compliance-cert-icon" />
              <h3>Ghana Data Protection</h3>
              <p>Data Protection Commission Registration</p>
              <span className="compliance-cert-status">Registered</span>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 5: COMPLIANCE COMMITMENTS */}
      {/* ======================================== */}
      <section className="compliance-commitments">
        <div className="compliance-container">
          <h2 className="compliance-section-title">Our Compliance <span className="gold">Commitments</span></h2>
          
          <div className="compliance-commitments-grid">
            <div className="compliance-commitment-card">
              <div className="compliance-commitment-number">01</div>
              <h3>Transparency</h3>
              <p>
                We are transparent about how we collect, use, and share your data. 
                Our privacy policy is written in plain language and regularly updated.
              </p>
            </div>
            
            <div className="compliance-commitment-card">
              <div className="compliance-commitment-number">02</div>
              <h3>Data Minimization</h3>
              <p>
                We only collect data that is necessary for providing our services. 
                You remain semi-anonymous - no ID verification required.
              </p>
            </div>
            
            <div className="compliance-commitment-card">
              <div className="compliance-commitment-number">03</div>
              <h3>Regular Audits</h3>
              <p>
                Independent third-party audits are conducted annually to verify 
                our security and compliance posture.
              </p>
            </div>
            
            <div className="compliance-commitment-card">
              <div className="compliance-commitment-number">04</div>
              <h3>Breach Notification</h3>
              <p>
                In the event of a data breach, we commit to notifying affected users 
                within 72 hours as required by law.
              </p>
            </div>
            
            <div className="compliance-commitment-card">
              <div className="compliance-commitment-number">05</div>
              <h3>Employee Training</h3>
              <p>
                All employees undergo annual data protection and security awareness 
                training.
              </p>
            </div>
            
            <div className="compliance-commitment-card">
              <div className="compliance-commitment-number">06</div>
              <h3>Vendor Management</h3>
              <p>
                All third-party vendors are vetted for security and compliance before 
                engagement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 6: REPORTING VIOLATIONS */}
      {/* ======================================== */}
      <section className="compliance-reporting">
        <div className="compliance-container">
          <div className="compliance-reporting-card">
            <div className="compliance-reporting-icon">
              <img src="/images/icons/feature-security.png" alt="Report" className="compliance-reporting-img" />
            </div>
            <h2>Report a Compliance Violation</h2>
            <p>
              If you believe there has been a violation of data protection laws, security breach, 
              or any compliance issue, please report it immediately.
            </p>
            <div className="compliance-reporting-methods">
              <div className="compliance-reporting-method">
                <strong>📧 Email:</strong> <a href="mailto:compliance@agyapadie.com">compliance@agyapadie.com</a>
              </div>
              <div className="compliance-reporting-method">
                <strong>📞 Phone:</strong> <a href="tel:+233302123456">+233 (0) 30 212 3456</a>
              </div>
              <div className="compliance-reporting-method">
                <strong>🔒 Secure Form:</strong> <Link to="/contact">Contact Us (Mark "Compliance" in subject)</Link>
              </div>
            </div>
            <p className="compliance-reporting-note">
              Whistleblower protections apply. Reports can be made anonymously.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 7: AUDIT & TRANSPARENCY */}
      {/* ======================================== */}
      <section className="compliance-audit">
        <div className="compliance-container">
          <div className="compliance-audit-grid">
            <div className="compliance-audit-content">
              <h2 className="compliance-section-title">Audit & <span className="gold">Transparency</span></h2>
              <p>
                We believe in radical transparency. Here are our latest audit reports and compliance documents.
              </p>
              
              <div className="compliance-audit-list">
                <div className="compliance-audit-item">
                  <span className="compliance-audit-name">Data Protection Impact Assessment</span>
                  <span className="compliance-audit-date">Last: March 2026</span>
                  <a href="#" className="compliance-audit-link">Download →</a>
                </div>
                <div className="compliance-audit-item">
                  <span className="compliance-audit-name">Security Audit Report</span>
                  <span className="compliance-audit-date">Last: February 2026</span>
                  <a href="#" className="compliance-audit-link">Download →</a>
                </div>
                <div className="compliance-audit-item">
                  <span className="compliance-audit-name">Data Protection Commission Registration</span>
                  <span className="compliance-audit-date">Certificate #: DPC/GH/2026/0421</span>
                  <a href="#" className="compliance-audit-link">View →</a>
                </div>
                <div className="compliance-audit-item">
                  <span className="compliance-audit-name">Terms of Service</span>
                  <span className="compliance-audit-date">Version: 2.0 (April 2026)</span>
                  <Link to="/terms" className="compliance-audit-link">View →</Link>
                </div>
              </div>
            </div>
            
            <div className="compliance-audit-badge">
              <div className="compliance-badge-large">
                <img src="/images/icons/feature-security.png" alt="Data Protection" className="compliance-badge-icon-img" />
                <h3>Data Protection</h3>
                <p>Commission Registered</p>
                <span className="compliance-badge-number">DPC/GH/2026/0421</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 8: CONTACT COMPLIANCE OFFICER */}
      {/* ======================================== */}
      <section className="compliance-officer">
        <div className="compliance-container">
          <div className="compliance-officer-card">
            <div className="compliance-officer-icon">
              <img src="/images/icons/feature-trustees.png" alt="DPO" className="compliance-officer-img" />
            </div>
            <div className="compliance-officer-content">
              <h3>Contact Our Data Protection Officer</h3>
              <p>
                For privacy-related questions, data subject requests, or compliance inquiries, 
                please contact our Data Protection Officer directly.
              </p>
              <div className="compliance-officer-info">
                <p><strong>Name:</strong> Abena Osei (DPO)</p>
                <p><strong>Email:</strong> <a href="mailto:dpo@agyapadie.com">dpo@agyapadie.com</a></p>
                <p><strong>Address:</strong> 123 Independence Avenue, Ridge, Accra, Ghana</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 9: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="compliance-cta">
        <div className="compliance-container">
          <div className="compliance-cta-content">
            <h2 className="compliance-cta-title">Have Questions About Compliance?</h2>
            <p className="compliance-cta-subtitle">
              Our legal and compliance team is here to help.
            </p>
            <div className="compliance-cta-buttons">
              <Link to="/contact" className="compliance-cta-primary">Contact Compliance Team</Link>
              <Link to="/privacy" className="compliance-cta-secondary">Read Privacy Policy</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .compliance-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .compliance-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        .white {
          color: white;
        }
        
        /* Hero Section */
        .compliance-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .compliance-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .compliance-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .compliance-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .compliance-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .compliance-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .compliance-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .compliance-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Section Titles */
        .compliance-section-title {
          font-size: 36px;
          text-align: center;
          margin-bottom: 20px;
          color: #0A0A0A;
        }
        
        .compliance-section-subtitle {
          font-size: 18px;
          text-align: center;
          color: #666;
          margin-bottom: 60px;
        }
        
        .compliance-section-subtitle.white {
          color: #ccc;
        }
        
        /* Regulatory Framework */
        .compliance-framework {
          padding: 80px 0;
          background: white;
        }
        
        .compliance-framework-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 30px;
        }
        
        .compliance-framework-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          transition: transform 0.3s;
        }
        
        .compliance-framework-card:hover {
          transform: translateY(-5px);
        }
        
        .compliance-framework-icon {
          height: 50px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .compliance-framework-card h3 {
          font-size: 20px;
          margin-bottom: 15px;
        }
        
        .compliance-framework-card p {
          color: #555;
          line-height: 1.6;
          margin-bottom: 15px;
        }
        
        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
        }
        
        .status-badge.compliant {
          background: #d1fae5;
          color: #065f46;
        }
        
        .status-badge.in-progress {
          background: #fed7aa;
          color: #9a3412;
        }
        
        /* Data Protection */
        .compliance-data-protection {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .compliance-data-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
        }
        
        .compliance-data-list {
          margin-top: 30px;
        }
        
        .compliance-data-item {
          display: flex;
          gap: 15px;
          margin-bottom: 25px;
        }
        
        .compliance-data-icon {
          height: 30px;
          width: auto;
        }
        
        .compliance-data-item h4 {
          font-size: 16px;
          margin-bottom: 5px;
        }
        
        .compliance-data-item p {
          font-size: 14px;
          color: #666;
        }
        
        .compliance-data-rights {
          background: white;
          padding: 30px;
          border-radius: 15px;
        }
        
        .compliance-data-rights h3 {
          font-size: 22px;
          margin-bottom: 20px;
          color: #0A0A0A;
        }
        
        .compliance-data-rights ul {
          list-style: none;
          margin-bottom: 20px;
        }
        
        .compliance-data-rights li {
          margin-bottom: 10px;
          color: #555;
        }
        
        .compliance-privacy-link {
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
        }
        
        /* Certifications */
        .compliance-certifications {
          padding: 80px 0;
          background: #0A0A0A;
        }
        
        .compliance-cert-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }
        
        .compliance-cert-card {
          background: rgba(255,255,255,0.05);
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .compliance-cert-card:hover {
          transform: translateY(-5px);
          background: rgba(255,255,255,0.1);
        }
        
        .compliance-cert-icon {
          height: 50px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .compliance-cert-card h3 {
          font-size: 20px;
          color: white;
          margin-bottom: 10px;
        }
        
        .compliance-cert-card p {
          font-size: 14px;
          color: #ccc;
          margin-bottom: 15px;
        }
        
        .compliance-cert-status {
          display: inline-block;
          padding: 4px 12px;
          background: rgba(255,215,0,0.2);
          color: #FFD700;
          border-radius: 20px;
          font-size: 12px;
        }
        
        /* Commitments */
        .compliance-commitments {
          padding: 80px 0;
          background: white;
        }
        
        .compliance-commitments-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .compliance-commitment-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          position: relative;
          transition: transform 0.3s;
        }
        
        .compliance-commitment-card:hover {
          transform: translateY(-5px);
        }
        
        .compliance-commitment-number {
          font-size: 48px;
          font-weight: bold;
          color: #FFD700;
          opacity: 0.3;
          margin-bottom: 15px;
        }
        
        .compliance-commitment-card h3 {
          font-size: 20px;
          margin-bottom: 15px;
        }
        
        .compliance-commitment-card p {
          color: #555;
          line-height: 1.6;
        }
        
        /* Reporting */
        .compliance-reporting {
          padding: 0 0 80px 0;
        }
        
        .compliance-reporting-card {
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          border-radius: 15px;
          padding: 40px;
          text-align: center;
        }
        
        .compliance-reporting-img {
          height: 50px;
          width: auto;
          margin-bottom: 20px;
        }
        
        .compliance-reporting-card h2 {
          font-size: 28px;
          color: white;
          margin-bottom: 15px;
        }
        
        .compliance-reporting-card p {
          color: #ccc;
          margin-bottom: 25px;
        }
        
        .compliance-reporting-methods {
          display: flex;
          justify-content: center;
          gap: 30px;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }
        
        .compliance-reporting-method {
          color: #ccc;
        }
        
        .compliance-reporting-method a {
          color: #FFD700;
          text-decoration: none;
        }
        
        .compliance-reporting-note {
          font-size: 12px;
          color: #888;
        }
        
        /* Audit */
        .compliance-audit {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .compliance-audit-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        
        .compliance-audit-list {
          margin-top: 30px;
        }
        
        .compliance-audit-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          padding: 15px 0;
          border-bottom: 1px solid #ddd;
        }
        
        .compliance-audit-name {
          font-weight: bold;
        }
        
        .compliance-audit-date {
          font-size: 12px;
          color: #666;
        }
        
        .compliance-audit-link {
          color: #FFD700;
          text-decoration: none;
          font-weight: bold;
        }
        
        .compliance-badge-large {
          background: white;
          padding: 40px;
          border-radius: 15px;
          text-align: center;
          border: 2px solid #FFD700;
        }
        
        .compliance-badge-icon-img {
          height: 50px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .compliance-badge-large h3 {
          font-size: 24px;
          margin: 15px 0 5px;
        }
        
        .compliance-badge-number {
          display: inline-block;
          margin-top: 10px;
          padding: 5px 15px;
          background: #F5F0E6;
          border-radius: 20px;
          font-family: monospace;
        }
        
        /* Compliance Officer */
        .compliance-officer {
          padding: 0 0 80px 0;
        }
        
        .compliance-officer-card {
          background: white;
          border: 1px solid #eee;
          border-radius: 15px;
          padding: 40px;
          display: flex;
          gap: 30px;
          align-items: center;
          flex-wrap: wrap;
          box-shadow: 0 5px 20px rgba(0,0,0,0.05);
        }
        
        .compliance-officer-img {
          height: 60px;
          width: auto;
        }
        
        .compliance-officer-content {
          flex: 1;
        }
        
        .compliance-officer-content h3 {
          font-size: 24px;
          margin-bottom: 10px;
        }
        
        .compliance-officer-content p {
          color: #666;
          margin-bottom: 15px;
        }
        
        .compliance-officer-info p {
          margin-bottom: 5px;
        }
        
        .compliance-officer-info a {
          color: #FFD700;
          text-decoration: none;
        }
        
        /* CTA */
        .compliance-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          text-align: center;
        }
        
        .compliance-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .compliance-cta-title {
          font-size: 32px;
          color: white;
          margin-bottom: 15px;
        }
        
        .compliance-cta-subtitle {
          font-size: 16px;
          color: #ccc;
          margin-bottom: 30px;
        }
        
        .compliance-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .compliance-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .compliance-cta-primary:hover {
          background: #B8860B;
        }
        
        .compliance-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .compliance-cta-secondary:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        /* Responsive */
        @media (max-width: 1024px) {
          .compliance-hero-title {
            font-size: 40px;
          }
          
          .compliance-framework-grid {
            grid-template-columns: 1fr;
          }
          
          .compliance-commitments-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .compliance-cert-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        @media (max-width: 768px) {
          .compliance-hero-title {
            font-size: 28px;
          }
          
          .compliance-section-title {
            font-size: 24px;
          }
          
          .compliance-data-grid {
            grid-template-columns: 1fr;
          }
          
          .compliance-audit-grid {
            grid-template-columns: 1fr;
          }
          
          .compliance-commitments-grid {
            grid-template-columns: 1fr;
          }
          
          .compliance-cert-grid {
            grid-template-columns: 1fr;
          }
          
          .compliance-reporting-methods {
            flex-direction: column;
            align-items: center;
          }
          
          .compliance-officer-card {
            text-align: center;
            flex-direction: column;
          }
          
          .compliance-cta-title {
            font-size: 24px;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default Compliance;